/** @format */

import React, { useMemo } from "react";
import Plot from "react-plotly.js";
import "../Style/slidepanel.css";
import TooltipInfoBox from "./TooltipInfoBox";
import { useEffect } from "react";

const SlidePanelPnL = ({ isOpen, onClose, damData, STRIKE, QUANTITY }) => {
  const [readyToRender, setReadyToRender] = React.useState(false);

  useEffect(() => {
    if (isOpen && damData.length > 0) {
      setReadyToRender(false); // reset
      const timeout = setTimeout(() => {
        setReadyToRender(true);
      }, 300); // περιμένεις να αποδώσει το panel σωστά

      return () => clearTimeout(timeout);
    }
  }, [isOpen, damData]);

  const monthlyData = useMemo(() => {
    if (!damData || damData.length === 0) return [];

    // STRIKE & QUANTITY are passed via props

    const grouped = {};

    // Ομαδοποίηση ανά μήνα πάνω σε ΩΡΙΑΙΑ δεδομένα
    damData.forEach((row) => {
      const d = new Date(row.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}`;

      const mcp = Number(row.avgMCP) || 0;

      if (!grouped[monthKey]) {
        grouped[monthKey] = { sum: 0, hours: 0 };
      }
      grouped[monthKey].sum += mcp; // άθροισμα ωριαίων MCP
      grouped[monthKey].hours += 1; // πλήθος ωρών που έχουμε για τον μήνα
    });

    return Object.entries(grouped)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([monthKey, info]) => {
        const [year, month] = monthKey.split("-");
        const date = new Date(`${year}-${month}-01`);
        const formatter = new Intl.DateTimeFormat("en-US", { month: "short" });

        const label = `${formatter.format(date)} ${year}`;

        // Μέσος DAM μήνα (μέσος ημερήσιος = μέσος ωριαίος)
        const avgDamRaw = info.sum / Math.max(info.hours, 1);
        const avgDam = Math.round(avgDamRaw * 100) / 100;

        // 🔹 ΥΠΟΛΟΓΙΣΜΟΣ ΩΡΩΝ ΜΗΝΑ
        let hoursInMonth = info.hours * 24;

        // Προσθέτουμε extra ώρα για τον Οκτώβρη 2025 (DST +1)
        if (year === "2025" && month === "10") {
          hoursInMonth += 1;
        }

        // (avg(DAM)_μήνα - STRIKE) * QUANTITY * ώρες_μήνα
        const totalRaw = (avgDam - STRIKE) * QUANTITY * hoursInMonth;
        const total = Math.round(totalRaw * 100) / 100;

        return {
          label,
          total,
          avgDam,
          year,
          month: formatter.format(date),
        };
      });
  }, [damData, STRIKE, QUANTITY]);

  const x = monthlyData.map((m) => m.label).concat("Total");
  const y = monthlyData.map((m) => m.total);
  const total = y.reduce((sum, v) => sum + v, 0);

  const data = [
    {
      type: "waterfall",
      measure: Array(y.length).fill("relative").concat("total"),
      x: x,
      y: y.concat(total),
      textposition: "outside",
      text: y.concat(total).map((val) =>
        val.toLocaleString("el-GR", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
        }),
      ),
      connector: { line: { color: "#777" } },
      decreasing: { marker: { color: "#dc3545" } },
      increasing: { marker: { color: "#28a745" } },
      totals: { marker: { color: "#71c5f8" } },
    },
  ];
  // const yValues = y.concat(total); // kept as reference if dynamic y-range needed

  //
  //
  //
  // Δείχνει τους τελευταίους 6 μήνες + Total· scroll αριστερά για παλαιότερους
  const VISIBLE_MONTHS = 6;
  const totalBars = x.length; // months + 1 Total
  const showSlider = monthlyData.length > VISIBLE_MONTHS;
  const rangeStart = showSlider ? totalBars - VISIBLE_MONTHS - 1 - 0.5 : -0.5;
  const rangeEnd = totalBars - 0.5;

  const layout = {
    plot_bgcolor: "#f4f4f4",
    paper_bgcolor: "#f4f4f4",

    width: undefined,
    height: 420,
    font: { family: "Segoe UI", color: "#333" },
    margin: { t: 40, b: showSlider ? 40 : 20, l: 40, r: 20 },
    xaxis: {
      tickangle: 0,
      tickfont: { size: 12 },
      automargin: true,
      tickmode: "array",
      tickvals: x,
      ticktext: x,
      ticklabelposition: "outside",
      anchor: "x",
      range: [rangeStart, rangeEnd],
      rangeslider: showSlider
        ? { visible: true, thickness: 0.06, bgcolor: "#ddd" }
        : { visible: false },
    },

    yaxis: {
      tickprefix: "€",
      gridcolor: "#444",
      autorange: true,
    },
  };

  // Derived values available if needed for badges/summaries
  // const totalPnL = monthlyData.reduce((acc, row) => acc + row.total, 0);
  // const avgDAM =
  //   monthlyData.reduce((acc, row) => acc + row.avgDam, 0) / monthlyData.length;

  return (
    <div className={`slide-panel ${isOpen ? "open" : ""}`}>
      <div className="slide-header">
        <div className="title-with-tooltip">
          <h2>VBL Monthly Summary</h2> <TooltipInfoBox />
        </div>
      </div>

      <div
        style={{
          width: "100%",
          paddingInline: "5px",
          boxSizing: "border-box",
          overflowX: "auto",
          marginTop: "-10px",
          marginBottom: "-30px",
        }}
      >
        {readyToRender && (
          <Plot
            data={data}
            layout={layout}
            useResizeHandler={true}
            config={{ responsive: true }}
            style={{ width: "100%", height: 520 }}
          />
        )}
      </div>
    </div>
  );
};

export default SlidePanelPnL;
